import React, { useEffect, useState } from 'react';
import { Heart, Target, Eye, Users } from 'lucide-react';
import { membroService } from '../services/api';
import './QuemSomos.css';

export default function QuemSomos() {
  const [membros,  setMembros]  = useState<any[]>([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    membroService.listar()
      .then(r => setMembros(Array.isArray(r.data) ? r.data : r.data.membros || []))
      .catch(() => setMembros([]))
      .finally(() => setLoading(false));
  }, []);

  const diretoria = membros.filter(m => m.tipo === 'diretoria' || !m.tipo);
  const conselho  = membros.filter(m => m.tipo === 'conselho');

  return (
    <main>
      <div className="page-hero">
        <h1>Quem Somos</h1>
        <p>Conheça a história, missão e as pessoas por trás do Instituto Mãos Estendidas.</p>
      </div>

      {/* Historia */}
      <section className="section-pad">
        <div className="container">
          <div className="qs-sobre-grid">
            <div>
              <div className="section-title" style={{ textAlign: 'left', marginBottom: 28 }}>
                <span className="label">Nossa História</span>
                <h2>Um instituto nascido da vontade de ajudar</h2>
              </div>
              <p style={{ marginBottom: 16 }}>
                O <strong>Instituto Mãos Estendidas (IME)</strong> é uma organização não governamental,
                de direito privado, constituída como sociedade civil, sem fins econômicos, sediada em
                Marabá – Pará, com atuação em todo o Estado do Pará.
              </p>
              <p style={{ marginBottom: 16 }}>
                Fundado em 31 de julho de 2021, o Instituto nasceu da vontade de um grupo de cidadãos
                comprometidos com o desenvolvimento social e humano de Marabá e região, atuando para atender
                a todos que se dirigem à entidade, independente de classe social, nacionalidade, sexo, raça,
                cor ou crença religiosa.
              </p>
              <p>
                Registrado no Cartório Michels de Marabá – PA (Protocolo R. 11.043 | Livro: A-112),
                o Instituto opera sob CNPJ 45.334.670/0001-82.
              </p>
            </div>
            <div className="qs-valores">
              {[
                { icon: <Heart size={20} fill="currentColor" />, title: 'Missão', text: 'Planejar, fomentar e coordenar atividades que beneficiem as comunidades locais nas áreas da saúde, assistência social, educação, proteção ao meio ambiente, cultura, esporte, turismo e direitos humanos.' },
                { icon: <Eye size={20} />, title: 'Visão', text: 'Ser referência no desenvolvimento social e humano em Marabá e região, promovendo equidade, educação e cultura para todos.' },
                { icon: <Target size={20} />, title: 'Valores', text: 'Legalidade, impessoalidade, moralidade, publicidade, economicidade e eficiência. Transparência, respeito à diversidade e compromisso com a comunidade.' },
              ].map((v, i) => (
                <div className="valor-card" key={i}>
                  <div className="valor-icon">{v.icon}</div>
                  <h4>{v.title}</h4>
                  <p>{v.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Finalidades */}
      <section className="section-pad" style={{ background: 'var(--creme-escuro)' }}>
        <div className="container">
          <div className="section-title">
            <span className="label">O que fazemos</span>
            <h2>Nossas Finalidades</h2>
            <p>O Instituto atua em múltiplas frentes para o desenvolvimento das comunidades.</p>
          </div>
          <div className="grid-3">
            {[
              { title: 'Saúde', desc: 'Ações pontuais de prevenção, combate às drogas e outras situações de risco à saúde das comunidades.' },
              { title: 'Educação', desc: 'Cursos, palestras, workshops, capacitações profissionais e assessoria para segmentos da sociedade.' },
              { title: 'Assistência Social', desc: 'Projetos de combate à pobreza, geração de emprego e renda, e atendimento a pessoas em vulnerabilidade.' },
              { title: 'Meio Ambiente', desc: 'Preservação, manutenção e restauração do meio ambiente ecologicamente equilibrado e desenvolvimento sustentável.' },
              { title: 'Cultura e Esporte', desc: 'Apoio a eventos culturais, artísticos, esportivos e manifestações culturais locais.' },
              { title: 'Direitos Humanos', desc: 'Promoção dos direitos de mulheres, indígenas, negros, idosos, LGBTQIA+, crianças e adolescentes.' },
            ].map((f, i) => (
              <div className="card" key={i} style={{ padding: 24, animation: `fadeUp 0.4s ease ${i * 0.08}s both` }}>
                <div style={{ width: 36, height: 36, background: '#fde8d8', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                  <Heart size={16} style={{ color: 'var(--laranja)' }} />
                </div>
                <h4 style={{ marginBottom: 8 }}>{f.title}</h4>
                <p style={{ fontSize: '0.9rem' }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Diretoria */}
      <section className="section-pad">
        <div className="container">
          <div className="section-title">
            <span className="label">Nossa equipe</span>
            <h2>Diretoria Executiva</h2>
          </div>
          {loading ? (
            <div className="loading-center"><div className="spinner" /></div>
          ) : membros.length === 0 ? (
            <div className="loading-center"><Users size={40} style={{ color: 'var(--borda)' }} /><p>Membros não cadastrados.</p></div>
          ) : (
            <>
              {diretoria.length > 0 && (
                <div className="grid-4" style={{ marginBottom: 48 }}>
                  {diretoria.map((m: any) => (
                    <div className="card qs-membro-card" key={m._id}>
                      <div className="qs-avatar">
                        {m.fotoUrl
                          ? <img src={m.fotoUrl} alt={m.nome} />
                          : <span>{m.nome?.charAt(0)}</span>
                        }
                      </div>
                      <h4>{m.nome}</h4>
                      <span className="qs-cargo">{m.cargo}</span>
                      {m.bio && <p>{m.bio}</p>}
                    </div>
                  ))}
                </div>
              )}
              {conselho.length > 0 && (
                <>
                  <h3 style={{ textAlign: 'center', marginBottom: 28 }}>Conselho Fiscal</h3>
                  <div className="grid-4">
                    {conselho.map((m: any) => (
                      <div className="card qs-membro-card" key={m._id}>
                        <div className="qs-avatar">
                          {m.fotoUrl
                            ? <img src={m.fotoUrl} alt={m.nome} />
                            : <span>{m.nome?.charAt(0)}</span>
                          }
                        </div>
                        <h4>{m.nome}</h4>
                        <span className="qs-cargo">{m.cargo}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </section>
    </main>
  );
}
